
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Download, Calendar, TrendingUp, Users, Package, DollarSign } from "lucide-react";
import { useAdvancedReporting } from "@/hooks/useAdvancedReporting";
import { motion, AnimatePresence } from "framer-motion";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const AdvancedReportGenerator = () => {
  const { reportData, isGenerating, generateReport, downloadReport, scheduleReport, clearReport } = useAdvancedReporting();
  const [reportType, setReportType] = useState<string>('');
  const [period, setPeriod] = useState<string>('');
  const [format, setFormat] = useState<string>('pdf');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);

  const reportTypes = [
    { value: 'sales', label: 'Sales Report', icon: TrendingUp },
    { value: 'customers', label: 'Customer Analysis', icon: Users },
    { value: 'products', label: 'Product Performance', icon: Package },
    { value: 'financial', label: 'Financial Summary', icon: DollarSign }
  ];

  const periods = [
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: 'quarter', label: 'Last 3 Months' },
    { value: 'year', label: 'Last 12 Months' }
  ];

  const metrics = [
    'Revenue Growth',
    'Customer Acquisition',
    'Order Volume',
    'Average Order Value',
    'Customer Lifetime Value',
    'Conversion Rate'
  ];

  const handleGenerateReport = () => {
    if (!reportType || !period) return;

    generateReport({
      type: reportType as any,
      period: period as any,
      format: format as any,
      metrics: selectedMetrics
    });
  };

  const handleMetricToggle = (metric: string) => {
    setSelectedMetrics(prev => 
      prev.includes(metric) 
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  return (
    <div className="space-y-6">
      {/* Report Configuration */}
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Generate Advanced Report</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Report Type Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {reportTypes.map((type) => {
              const IconComponent = type.icon;
              return (
                <motion.div
                  key={type.value}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    reportType === type.value 
                      ? 'border-admin-accent-blue bg-blue-50' 
                      : 'border-admin-border hover:border-admin-accent-blue/50'
                  }`}
                  onClick={() => setReportType(type.value)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-3">
                    <IconComponent className="h-6 w-6 text-admin-accent-blue" />
                    <span className="font-medium text-admin-foreground">{type.label}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Period and Format Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-admin-foreground mb-2 block">
                Time Period
              </label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger>
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-admin-foreground mb-2 block">
                Export Format
              </label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button 
                onClick={handleGenerateReport}
                disabled={!reportType || !period || isGenerating}
                className="w-full bg-admin-accent-blue hover:bg-admin-accent-blue/90"
              >
                {isGenerating ? 'Generating...' : 'Generate Report'}
              </Button>
            </div>
          </div>

          {/* Metrics Selection */}
          <div>
            <label className="text-sm font-medium text-admin-foreground mb-3 block">
              Include Metrics
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {metrics.map((metric) => (
                <div key={metric} className="flex items-center space-x-2">
                  <Checkbox
                    id={metric}
                    checked={selectedMetrics.includes(metric)}
                    onCheckedChange={() => handleMetricToggle(metric)}
                  />
                  <label 
                    htmlFor={metric}
                    className="text-sm text-admin-foreground cursor-pointer"
                  >
                    {metric}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Generated Report Display */}
      <AnimatePresence>
        {reportData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Report Header */}
            <Card className="admin-card border-admin-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-admin-foreground">{reportData.title}</CardTitle>
                    <p className="text-sm text-admin-muted-foreground mt-1">
                      Generated on {new Date(reportData.generatedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => downloadReport(reportData, format as any)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => scheduleReport({ type: reportType as any, period: period as any, format: format as any }, 'weekly')}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Schedule
                    </Button>
                    <Button variant="outline" onClick={clearReport}>
                      Clear
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Report Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="admin-card border-admin-border">
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-admin-foreground">
                      ${reportData.summary.totalRevenue.toFixed(2)}
                    </p>
                    <p className="text-sm text-admin-muted-foreground">Total Revenue</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="admin-card border-admin-border">
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-admin-foreground">
                      {reportData.summary.totalOrders}
                    </p>
                    <p className="text-sm text-admin-muted-foreground">Total Orders</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="admin-card border-admin-border">
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-admin-foreground">
                      ${reportData.summary.averageOrderValue.toFixed(2)}
                    </p>
                    <p className="text-sm text-admin-muted-foreground">Avg Order Value</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="admin-card border-admin-border">
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-lg font-bold text-admin-foreground truncate">
                      {reportData.summary.topProduct}
                    </p>
                    <p className="text-sm text-admin-muted-foreground">Top Product</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="admin-card border-admin-border">
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${
                      reportData.summary.growthRate >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {reportData.summary.growthRate >= 0 ? '+' : ''}{reportData.summary.growthRate.toFixed(1)}%
                    </p>
                    <p className="text-sm text-admin-muted-foreground">Growth Rate</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {reportData.charts.map((chart, index) => (
                <Card key={index} className="admin-card border-admin-border">
                  <CardHeader>
                    <CardTitle className="text-admin-foreground">{chart.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      {chart.type === 'line' ? (
                        <LineChart data={chart.data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} />
                        </LineChart>
                      ) : (
                        <BarChart data={chart.data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="orders" fill="#10b981" />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Insights and Recommendations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="admin-card border-admin-border">
                <CardHeader>
                  <CardTitle className="text-admin-foreground">Key Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {reportData.insights.map((insight, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <Badge variant="secondary" className="mt-0.5">
                          {index + 1}
                        </Badge>
                        <p className="text-admin-foreground">{insight}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="admin-card border-admin-border">
                <CardHeader>
                  <CardTitle className="text-admin-foreground">Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {reportData.recommendations.map((rec, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <Badge variant="outline" className="mt-0.5">
                          {index + 1}
                        </Badge>
                        <p className="text-admin-foreground">{rec}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdvancedReportGenerator;
